<script setup lang="ts">
import Card from '@/components/Card/card-container.vue'
import CardHeader from '@/components/Card/card-header.vue'
import CardHeaderItem from '@/components/Card/card-header-item.vue'
import CardContent from '@/components/Card/card-content.vue'
import InputText from '@/components/input-text.vue'
import InputNumber from '@/components/input-number.vue'
import DropdownContext from '@/components/dropdown-context.vue'
import DropdownItem from '@/components/dropdown-item.vue'
import { computed, toRef } from 'vue'
import { calcCreditNoteTotals } from '@/features/credit-notes/utils/totals'
import { roundBillingRands, roundBillingQuantity, formatRands, formatNumber, calculateBillingLineItemTotalInRands, Logger } from '@/shared'
import { watchDebounced } from '@vueuse/core'

type Line = { id?: number; description: string; kWh: number; tariffPerKWh: number }

const props = withDefaults(
  defineProps<{
    lines: Line[]
    vatPercentage: number
    canDelete: boolean
    readonly?: boolean
  }>(),
  { readonly: false }
)

const emit = defineEmits<{
  add: []
  remove: [index: number]
  'update:line': [{ index: number; line: Line }]
}>()

function updateLineItem({ index, line }: { index: number, line: Line }) {
  emit('update:line', { index, line })
}

watchDebounced(
  () => props.lines,
  lineItems => {
    Logger.trace(
      `Line items were updated and totals have been recalculated`,
      {
        totals: Object.values(lineItems).flat().map(
          item => ({
            description: item.description,
            quantity: roundBillingQuantity(item.kWh),
            rateInRands: roundBillingRands(item.tariffPerKWh),
            total: calculateBillingLineItemTotalInRands({ quantity: item.kWh, rateInRands: item.tariffPerKWh })
          })
        )
      }
    )
  },
  { debounce: 500 }
)

const totals = computed(() => calcCreditNoteTotals(props.lines, props.vatPercentage))
const subTotalCents = computed(() => totals.value.subTotalCents)
const vatCents = computed(() => totals.value.vatCents)
const totalCents = computed(() => totals.value.totalCents)
</script>

<template>
  <Card class="credit-note">
    <CardHeader>
      <CardHeaderItem>CREDIT NOTE</CardHeaderItem>
      <CardHeaderItem />
    </CardHeader>

    <CardContent>
      <table class="table table-no-bottom-border w-100" :class="$style.linesTable">
        <thead>
          <tr>
            <th scope="col" style="width: 55%">Description</th>
            <th scope="col" class="text-end" style="width: 15%">kWh</th>
            <th scope="col" class="text-end" style="width: 15%">Tariff (R/kWh)</th>
            <th scope="col" class="text-end" style="width: 15%">Subtotal (R)</th>
            <th style="width: 2rem"></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(l, i) in props.lines" :key="l.id ?? i">
            <td>
              <InputText
                :value="l.description"
                placeholder="Please type here"
                :disabled="props.readonly"
                @update:value="
                  v =>
                    updateLineItem({ index: i, line: { ...l, description: String(v ?? '') } })
                " />
            </td>

            <td class="text-end">
              <InputNumber
                numeric-only
                :value="l.kWh"
                :min="0"
                :disabled="props.readonly"
                @update:value="
                  v => updateLineItem({ index: i, line: { ...l, kWh: Number(v || 0) } })
                " />
            </td>

            <td class="text-end">
              <InputNumber
                numeric-only
                :value="l.tariffPerKWh"
                :min="0"
                :disabled="props.readonly"
                @update:value="
                  v =>
                    updateLineItem({ index: i, line: { ...l, tariffPerKWh: Number(v || 0) } })
                " />
            </td>

            <td class="text-end">{{
                formatRands(calculateBillingLineItemTotalInRands({ quantity: l.kWh, rateInRands: l.tariffPerKWh }), { maxScale: 2 })
              }}</td>

            <td class="text-right" :class="$style.menuCell">
              <DropdownContext v-if="props.canDelete">
                <DropdownItem @click="emit('remove', i)">Delete</DropdownItem>
              </DropdownContext>
            </td>
          </tr>

          <tr>
            <td :colspan="5" class="px-2 pt-2 pb-3">
              <a role="button" class="text-teal fw-semibold" @click="emit('add')">+ New Charge</a>
            </td>
          </tr>
        </tbody>
      </table>
    </CardContent>

    <footer class="card-footer" :class="$style.cardFooter">
      <div :class="$style.cnFooter">
        <span :class="$style.label">Sub Total</span>
        <span :class="$style.value">{{ formatRands(subTotalCents / 100, { maxScale: 2 }) }}</span>

        <span :class="$style.label">Total VAT ({{ formatNumber(vatPercentage, { minScale: 0 }) }}%)</span>
        <span :class="$style.value">{{ formatRands(vatCents / 100, { maxScale: 2 }) }}</span>

        <span :class="[$style.label, $style.emph]">Credit (Incl. VAT)</span>
        <span :class="[$style.value, $style.emph]">{{ formatRands(totalCents / 100, { maxScale: 2 }) }}</span>
      </div>
    </footer>
  </Card>
</template>

<style module>
.card-footer {
  background: #eeeeee;
  border-top: 1px solid #e0e0e0;
  padding: 12px 16px;
}

.cn-footer {
  display: grid;
  grid-template-columns: auto 160px;
  gap: 6px 28px;
  justify-content: end;
  width: 100%;
}

.label {
  color: #6b7280;
  text-align: right;
  min-width: 220px;
}

.value {
  text-align: right;
}
.emph {
  font-weight: 600;
}

:deep(thead th) {
  vertical-align: bottom;
}

.menu-cell {
  position: relative;
  overflow: visible;
}
</style>
